import "../../assets/ContentEditor.css";
import { TipTapEditor } from "../content-editor/tiptap-editor";
import { useEffect, useRef, useState } from "react";
import { useManagedModuleEditor } from "../../providers/managed-module-management-provider";
import { Button } from "primereact/button";
import { DebouncedInput } from "../debounced-input";
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from "primereact/dropdown";

export function FragmentInspector() {
  const { managedModule, fragments, selectedFragmentKey, updateFragment, updateFragmentHtml, flushEditorChanges,
    validationTarget, editor } = useManagedModuleEditor();
  const rootRef = useRef<HTMLDivElement>(null);
  const [viewMode, setViewMode] = useState<"text" | "html" | "instructions" | "aihint">("html");

  const [activeTheme, setActiveTheme] = useState<string>("");
  
  useEffect(() => {
    if (!activeTheme && managedModule.theme && managedModule.theme.length > 0) {
      setActiveTheme(managedModule.theme[0].value);
    }
  }, [managedModule.theme, activeTheme]);

  const fragment = (fragments ?? []).find(f => f.key === selectedFragmentKey);

  useEffect(() => {
    const issue = validationTarget?.issue;
    if (issue?.scope !== 'fragment' || issue.fragmentKey !== selectedFragmentKey) return;
    if (issue.field === 'instructions') setViewMode('instructions');
    if (issue.field === 'content') setViewMode('html');
  }, [validationTarget, selectedFragmentKey]);

  useEffect(() => {
    const issue = validationTarget?.issue;
    if (issue?.scope !== 'fragment' || issue.fragmentKey !== fragment?.key || issue.field === 'actions') return;
    if (issue.field === 'instructions' && viewMode !== 'instructions') return;
    if (issue.field === 'content' && viewMode !== 'html') return;
    const frame = requestAnimationFrame(() => {
      const selector = issue.field === 'title' ? '#fragment-title' : issue.field === 'instructions'
        ? '#fragment-instructions' : '[contenteditable="true"]';
      const input = rootRef.current?.querySelector<HTMLElement>(selector);
      input?.scrollIntoView?.({ block: 'nearest' });
      if (issue.field === 'content' && editor) editor.commands.focus();
      else input?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [validationTarget, fragment?.key, viewMode, editor]);

  if (!fragment) {
    return (
      <div className="p-3 text-color-secondary">
        Select a fragment to edit its content
      </div>
    );
  }

  const handleTitleChange = (value: string) => {
    updateFragment({ ...fragment, title: value });
  };

  const handleInstructionsChange = (value: string) => {
    updateFragment({ ...fragment, instructions: value });
  };

  const handleAiHintChange = (value: string) => {
    updateFragment({ ...fragment, aiHint: value.trim() || null });
  };

  const handleHtmlChange = (html: string) => {
    updateFragmentHtml(fragment.key, html);
  };

  const changeViewMode = (mode: typeof viewMode) => {
    flushEditorChanges();
    setViewMode(mode);
  };

  const handleHiddenChange = (hidden: boolean) => {
    updateFragment({ ...fragment, hidden: hidden ? 1 : 0 });
  };

  return (
    <div ref={rootRef} className="flex flex-column gap-3 p-3 surface-card border-round h-full">
      <div className="flex flex-row gap-3 min-h-0" style={{ display: 'flex', width: '100%', gap: '10px', alignItems: 'center' }}>
        <label htmlFor="fragment-title" className="font-semibold">Title</label>
        <DebouncedInput
            id="fragment-title"
            isTextArea={false}
            initialValue={fragment.title} 
            onSync={handleTitleChange}
            placeholder="Fragment title"
            style={{ flexGrow: 1 }}
        />
        <Checkbox
            checked={Boolean(fragment.hidden)}
            onChange={(e) => handleHiddenChange(e.checked ? true : false)}
            title="The module is hidden and not accessible via Back/Next buttons"
        />
        <label htmlFor="hidden-checkbox" className="cursor-pointer text-sm">Hidden</label>
      </div>

      <div className="flex align-items-center gap-2">
        <Checkbox inputId="fragment-fill-once" checked={Boolean(fragment.fillOnce)}
          onChange={e => updateFragment({ ...fragment, fillOnce: Boolean(e.checked) })} />
        <label htmlFor="fragment-fill-once" className="cursor-pointer text-sm">Fill in once</label>
        <span className="text-sm text-color-secondary">The first nonempty submission permanently locks this form for the learner.</span>
      </div>

      <div className="flex flex-column flex-1 min-h-0 h-full" style={{ overflow: 'hidden' }}>
        <label className="font-semibold mb-1">Content</label>
        <div className="flex align-items-center gap-2 mb-3">
            <Button
                label="Design"
                icon="pi pi-eye"
                className={viewMode === "html" ? "p-button-primary" : "p-button-outlined"}
                onClick={() => changeViewMode("html")}
                style={{ width: "auto" }}
            />
            <Button
                label="Source&nbsp;text"
                icon="pi pi-code"
                className={viewMode === "text" ? "p-button-primary" : "p-button-outlined"}
                onClick={() => changeViewMode("text")}
                style={{ width: "auto" }}
            />
            <Button
                label="Instructions"
                icon="pi pi-code"
                className={viewMode === "instructions" ? "p-button-primary" : "p-button-outlined"}
                onClick={() => changeViewMode("instructions")}
                style={{ width: "auto" }}
            />
            <Button
                label="AI hint"
                icon="pi pi-lightbulb"
                className={viewMode === "aihint" ? "p-button-primary" : "p-button-outlined"}
                onClick={() => changeViewMode("aihint")}
                style={{ width: "auto" }}
            />
            <div className="ml-auto flex align-items-center gap-2">
              <Dropdown 
                value={activeTheme}
                options={managedModule.theme ?? []}
                optionLabel="label"
                optionValue="value"
                onChange={(e) => setActiveTheme(e.value)}
                placeholder="No theme selected"
                className="w-12rem"
              />
            </div>
        </div>

        {viewMode === "text" && (
            <DebouncedInput
                isTextArea={true}
                initialValue={fragment.html ?? ""}
                onSync={handleHtmlChange}
                style={{ width: '100%', height: '100%', border: 'none' }}
                syncStrategy="blur"
            />
        )}
        
        {/* Changed: Always rendered but hidden by CSS to prevent unmounting */}
        <div style={{ display: viewMode === "html" ? 'flex' : 'none', flex: 1, flexDirection: 'column', height: '100%' }}>
            <TipTapEditor 
                key={fragment.key} 
                value={fragment.html} 
                theme={activeTheme} 
                onChange={handleHtmlChange} 
            />
        </div>

        {viewMode === "instructions" && (
            <div key={fragment.key} className="flex flex-column gap-3 flex-1" style={{ minHeight: 0, overflowY: 'auto' }}>
                <div className="flex flex-column flex-1" style={{ minHeight: '10rem' }}>
                    <label htmlFor="fragment-instructions" className="font-semibold mb-2">Fragment instructions</label>
                    <DebouncedInput id="fragment-instructions" isTextArea
                        initialValue={fragment.instructions ?? ''} onSync={handleInstructionsChange}
                        style={{ width: '100%', flex: 1, resize: 'none' }} syncStrategy="blur" />
                </div>
                <div className="flex flex-column flex-1" style={{ minHeight: '10rem' }}>
                    <label htmlFor="fragment-answers-explanation" className="font-semibold mb-2">Answers explanation</label>
                    <DebouncedInput id="fragment-answers-explanation" isTextArea
                        initialValue={fragment.answers_explanation ?? ''}
                        onSync={value => updateFragment({ ...fragment, answers_explanation: value || null })}
                        style={{ width: '100%', flex: 1, resize: 'none' }} syncStrategy="blur" />
                </div>
            </div>
        )}
        {viewMode === "aihint" && (
            <DebouncedInput
                key={fragment.key}
                aria-label="Fragment AI hint (optional)"
                placeholder="Optional guidance for AI when explaining this fragment"
                isTextArea={true}
                initialValue={fragment.aiHint ?? ""}
                onSync={handleAiHintChange}
                style={{ width: '100%', height: '100%', border: 'none' }}
                syncStrategy="blur"
            />
        )}
      </div>
    </div>
  );
}
